import { memo, useCallback, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { DatePicker } from 'core-components/library/datepicker'
import { Dropdown } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import { MultiSelect } from 'core-components/library/multiSelect'
import { BaseCrud, Static } from 'core-components/types'

import { getMultiSelectFilterValue } from '../../lib/filterValue'
import { DateRangeValue, FilterConfig, FilterValue, RangeValue } from '../../types'

import styles from './FilterValueEditor.module.scss'

interface FilterValueEditorProps {
  config: FilterConfig
  value: FilterValue
  onApply: (value: FilterValue) => void
  onCancel: () => void
}

export const FilterValueEditor = memo<FilterValueEditorProps>(
  ({ config, value, onApply, onCancel }) => {
    const [localValue, setLocalValue] = useState<FilterValue>(value)

    const handleApply = useCallback(() => {
      onApply(localValue)
    }, [onApply, localValue])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleApply()
        } else if (e.key === 'Escape') {
          onCancel()
        }
      },
      [handleApply, onCancel]
    )

    const dropdownSource = useMemo(() => {
      if ((config.type === 'select' || config.type === 'multiSelect') && config.options) {
        const source = new BaseCrud({
          fetcher: new Static(
            config.options.map((opt) => ({
              value: opt.value,
              text: opt.label,
            }))
          ),
          keyField: 'value',
        })
        source.setPaging({ page: 0, pageSize: 10000 })
        return source
      }
      return null
    }, [config])

    const renderEditor = () => {
      switch (config.type) {
        case 'text':
          return (
            <Input
              value={(localValue as string) || ''}
              onChange={(val) => setLocalValue(val)}
              placeholder={config.placeholder || `Enter ${config.label.toLowerCase()}...`}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          )

        case 'select':
          if (!dropdownSource) return null
          return (
            <Dropdown
              source={dropdownSource}
              value={(localValue as string | number) || ''}
              onChange={(val) => setLocalValue(val)}
              caption="text"
              placeholder={config.placeholder || `Select ${config.label.toLowerCase()}...`}
            />
          )

        case 'multiSelect':
          if (!dropdownSource) return null
          return (
            <MultiSelect
              caption="text"
              closeOnSelect={false}
              noFilterOnSelect
              onChange={(nextValue) => setLocalValue(nextValue.length ? nextValue : null)}
              placeholder={config.placeholder || `Select ${config.label.toLowerCase()}...`}
              source={dropdownSource}
              value={getMultiSelectFilterValue(localValue)}
            />
          )

        case 'date':
          return (
            <DatePicker
              mode="single"
              value={localValue instanceof Date ? localValue : undefined}
              onChange={(date) => setLocalValue(date)}
              label={config.placeholder || config.label}
            />
          )

        case 'dateRange': {
          const dateRangeVal = (localValue as DateRangeValue) || { from: null, to: null }
          return (
            <div className={styles.rangeInputs}>
              <DatePicker
                mode="single"
                value={dateRangeVal.from instanceof Date ? dateRangeVal.from : undefined}
                onChange={(date) => setLocalValue({ ...dateRangeVal, from: date || null })}
                label="From"
              />
              <span className={styles.rangeSeparator}>–</span>
              <DatePicker
                mode="single"
                value={dateRangeVal.to instanceof Date ? dateRangeVal.to : undefined}
                onChange={(date) => setLocalValue({ ...dateRangeVal, to: date || null })}
                label="To"
              />
            </div>
          )
        }

        case 'range': {
          const rangeVal = (localValue as RangeValue) || { from: null, to: null }
          return (
            <div className={styles.rangeInputs}>
              <Input
                value={rangeVal.from !== null ? String(rangeVal.from) : ''}
                onChange={(val) => setLocalValue({ ...rangeVal, from: val || null })}
                placeholder={config.placeholder || 'From'}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <span className={styles.rangeSeparator}>–</span>
              <Input
                value={rangeVal.to !== null ? String(rangeVal.to) : ''}
                onChange={(val) => setLocalValue({ ...rangeVal, to: val || null })}
                placeholder="To"
                onKeyDown={handleKeyDown}
              />
            </div>
          )
        }

        default:
          return null
      }
    }

    return (
      <div className={styles.root}>
        <div className={styles.editorLabel}>{config.label}</div>
        <div className={styles.editor}>{renderEditor()}</div>
        <div className={styles.actions}>
          <Button variant="outlined" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    )
  }
)

FilterValueEditor.displayName = 'FilterValueEditor'
